import { formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
} from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useListDemandReviewWorkspacesQuery } from '@/services/api/demandReviewService';
import {
  DemandReviewWorkspace,
  DemandReviewWorkspaceStatus,
} from '@/services/types/demand-review-types';

import { CreateDemandReviewDialog } from './CreateDemandReviewDialog';

interface DemandReviewListPageProps {
  matterId: string;
}

const statusConfig: Record<
  DemandReviewWorkspaceStatus,
  {
    label: string;
    icon: React.ElementType;
    variant: 'default' | 'secondary' | 'destructive' | 'outline';
  }
> = {
  [DemandReviewWorkspaceStatus.CREATED]: {
    label: 'Created',
    icon: Clock,
    variant: 'secondary',
  },
  [DemandReviewWorkspaceStatus.UPLOADING_DEMAND]: {
    label: 'Uploading',
    icon: Loader2,
    variant: 'secondary',
  },
  [DemandReviewWorkspaceStatus.TRANSFORMING]: {
    label: 'Processing',
    icon: Loader2,
    variant: 'secondary',
  },
  [DemandReviewWorkspaceStatus.PARSING_CITATIONS]: {
    label: 'Parsing Citations',
    icon: Loader2,
    variant: 'secondary',
  },
  [DemandReviewWorkspaceStatus.READY_FOR_EXHIBITS]: {
    label: 'Ready for Exhibits',
    icon: CheckCircle2,
    variant: 'default',
  },
  [DemandReviewWorkspaceStatus.MATCHING_CITATIONS]: {
    label: 'Matching Citations',
    icon: Loader2,
    variant: 'secondary',
  },
  [DemandReviewWorkspaceStatus.READY_FOR_VERIFICATION]: {
    label: 'Ready to Verify',
    icon: CheckCircle2,
    variant: 'default',
  },
  [DemandReviewWorkspaceStatus.VERIFYING]: {
    label: 'Verifying',
    icon: Loader2,
    variant: 'secondary',
  },
  [DemandReviewWorkspaceStatus.COMPLETED]: {
    label: 'Completed',
    icon: CheckCircle2,
    variant: 'default',
  },
  [DemandReviewWorkspaceStatus.FAILED]: {
    label: 'Failed',
    icon: AlertCircle,
    variant: 'destructive',
  },
};

export const DemandReviewListPage: React.FC<DemandReviewListPageProps> = ({
  matterId,
}) => {
  const navigate = useNavigate();
  const {
    data: workspaces,
    isLoading,
    error,
  } = useListDemandReviewWorkspacesQuery({ matterId });

  const handleWorkspaceClick = (workspaceId: string) => {
    navigate(`/matters/${matterId}/document-review/${workspaceId}`);
  };

  const handleCreateSuccess = (workspaceId: string) => {
    navigate(`/matters/${matterId}/document-review/${workspaceId}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-destructive">
        <AlertCircle className="h-12 w-12 mb-4" />
        <p>Failed to load document reviews</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Document Review
          </h1>
          <p className="text-muted-foreground mt-1">
            Upload and review demand documents with AI-powered citation
            verification
          </p>
        </div>
        <CreateDemandReviewDialog
          matterId={matterId}
          onSuccess={handleCreateSuccess}
        />
      </div>

      {workspaces && workspaces.length > 0 ? (
        <div className="grid gap-4">
          {workspaces.map((workspace: DemandReviewWorkspace) => {
            const status = statusConfig[workspace.status];
            const StatusIcon = status.icon;
            const isProcessing =
              workspace.status ===
                DemandReviewWorkspaceStatus.UPLOADING_DEMAND ||
              workspace.status === DemandReviewWorkspaceStatus.TRANSFORMING ||
              workspace.status ===
                DemandReviewWorkspaceStatus.PARSING_CITATIONS ||
              workspace.status ===
                DemandReviewWorkspaceStatus.MATCHING_CITATIONS ||
              workspace.status === DemandReviewWorkspaceStatus.VERIFYING;

            return (
              <Card
                key={workspace.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleWorkspaceClick(workspace.id)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <FileText className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">
                          {workspace.name}
                        </CardTitle>
                        <CardDescription>
                          Created{' '}
                          {formatDistanceToNow(new Date(workspace.created_at), {
                            addSuffix: true,
                          })}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge
                      variant={status.variant}
                      className="flex items-center gap-1"
                    >
                      <StatusIcon
                        className={`h-3 w-3 ${isProcessing ? 'animate-spin' : ''}`}
                      />
                      {status.label}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6 text-sm text-muted-foreground">
                    <span>{workspace.citation_count ?? 0} citations found</span>
                    {workspace.exhibit_count !== undefined && (
                      <span>{workspace.exhibit_count} exhibits</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">
              No document reviews yet
            </h3>
            <p className="text-muted-foreground text-center mb-4 max-w-md">
              Upload a demand document to start reviewing citations and
              verifying exhibit references with AI assistance.
            </p>
            <CreateDemandReviewDialog
              matterId={matterId}
              onSuccess={handleCreateSuccess}
              trigger={<Button>Create Your First Review</Button>}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DemandReviewListPage;
